import CircleIcon from '@mui/icons-material/Circle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Skeleton, Stack, type Theme, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import InfoBanner from '/@/components/bayer/info-banner';
import { useFieldsDashboard, useRecommendationsDashboard } from '/@/hooks/dashboards';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { getNodeScopeFilters, type NodeScopeFilters } from '/@/utils/node-scope';
import DashboardCard from '../../../../../components/bayer/dashboard-card';

import CreateRecommendationSection from '../../../rv/components/create-recommendation-section';

const RecommendationsDashboardCard = ({
    setOpenCreateRecommendationModal,
    userNodeId,
    nodeScope = getNodeScopeFilters('myFields'),
}: {
    setOpenCreateRecommendationModal: React.Dispatch<React.SetStateAction<boolean>>;
    userNodeId: number;
    nodeScope?: NodeScopeFilters;
}) => {
    const country = useSelector(getSessionCountry);
    const fieldsDashboardQuery = useFieldsDashboard({
        country: country.code,
        node: userNodeId,
    });
    const recommendationDashboardQuery = useRecommendationsDashboard({
        node: userNodeId,
        ...nodeScope,
    });
    const fieldsWithRecoPercentage = useMemo(() => {
        if (!fieldsDashboardQuery.data?.count) {
            return 0;
        }
        return Math.round(
            ((fieldsDashboardQuery.data?.withRecommendations || 0) / (fieldsDashboardQuery.data?.count || 0)) * 100
        );
    }, [fieldsDashboardQuery.data]);

    const handleClickNewReco = () => {
        mpTrackEvent('RV recommendations pop up opened', { entry_source: 'activity-section' });
        setOpenCreateRecommendationModal(true);
    };

    if (recommendationDashboardQuery.isValidating) {
        return (
            <DashboardCard title={_('Recomendaciones')} isLoading={true}>
                <Skeleton height={'100%'} variant='rectangular' />
            </DashboardCard>
        );
    }

    if (!recommendationDashboardQuery.data?.count) {
        return (
            <DashboardCard title={_('Recomendaciones')}>
                <CreateRecommendationSection setOpenCreateRecommendationModal={setOpenCreateRecommendationModal} />
            </DashboardCard>
        );
    }

    const cropProtectionRecommendationsCount =
        (recommendationDashboardQuery.data?.countByType?.funguicide || 0) +
        (recommendationDashboardQuery.data?.countByType?.insecticide || 0) +
        (recommendationDashboardQuery.data?.countByType?.herbicide || 0);

    const subtitleContent = (
        <Stack direction={'row'} spacing={2}>
            <Typography
                color='primary.mui.inactive'
                variant='body2'
                sx={{
                    display: 'inline',
                }}
            >
                <CircleIcon fontSize='small' sx={{ color: 'primary.onSurface', marginRight: 1 }} />
                {_('{{notCpRecosCount}} de híbridos', {
                    notCpRecosCount:
                        (recommendationDashboardQuery.data?.countByType?.corn || 0) +
                        (recommendationDashboardQuery.data?.countByType?.soy || 0) +
                        (recommendationDashboardQuery.data?.countByType?.density || 0),
                })}
            </Typography>
            <Typography
                color='primary.mui.inactive'
                variant='body2'
                sx={{
                    display: 'inline',
                }}
            >
                <CircleIcon fontSize='small' sx={{ color: 'primary.onSurface', marginRight: 1 }} />
                {_('{{cpRecosCount}} de proteccion de cultivos', {
                    cpRecosCount: cropProtectionRecommendationsCount,
                })}
            </Typography>
        </Stack>
    );

    return (
        <DashboardCard
            title={_('Recomendaciones')}
            subtitle={subtitleContent}
            isLoading={recommendationDashboardQuery.isLoading}
            buttonProps={{
                children: _('Nueva Recomendación'),
                onClick: handleClickNewReco,
                color: 'primary',
            }}
            number={recommendationDashboardQuery.data?.count}
        >
            <InfoBanner
                color='inactive'
                icon={<TrendingUpIcon fontSize='large' sx={{ fill: (t: Theme) => t.palette.primary.main }} />}
            >
                {_('{{fieldsWithRecoPercentage}}% de tus lotes tiene una recomendación', {
                    fieldsWithRecoPercentage: fieldsWithRecoPercentage,
                })}
            </InfoBanner>
        </DashboardCard>
    );
};

export default RecommendationsDashboardCard;
